<template>
  <UiButton
    theme="senary"
    :size="theme === 'secondary' ? 'small' : 'extra-big'"
    stretch
    :description="computedProBtn?.description"
    class="subscribe-button"
    @click="handleClick"
  >
    {{ computedProBtn?.text }}
  </UiButton>
</template>

<script setup lang="ts">
  import { storeToRefs } from 'pinia'
  import { useStorage } from '@vueuse/core'
  import UiButton from '@academica-box/components/Buttons/UiButton/UiButton.vue'
  import { pluralize } from '@academica-box/utils/scripts/pluralize'
  import { useSubscribesStore } from '@academica-box/composables/store/subscribes'
  import { useUserStore } from '@academica-box/composables/store/user'
  import { useLandingStore } from '@/store/landing'
  import { SmartComponents, useSmartComponentsStore } from '@/store/smartComponents'
  import { AccessTypeEnum } from '@/helpers/mappingCourseData'
  import { sendLandingClickSubscribeButton } from '~/analytics/events'

  const isSubscribeOpenModal = useStorage('is_subscribe_open_modal', false)
  const subscribesStore = useSubscribesStore()
  const { activeSubscription, userSubsription, isUserSubscriptionActive, openedCourses } = storeToRefs(subscribesStore)
  const smartComponentsStore = useSmartComponentsStore()
  const landingStore = useLandingStore()
  const { landing } = storeToRefs(landingStore)
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)

  const props = withDefaults(defineProps<{
    theme?: 'primary' | 'secondary';
  }>(), {
    theme: 'primary',
  })

  const hasCourseInSubscriptions = computed(() => {
    const nomenclatureId = landing?.value?.commonData.nomenclature_id

    if (!nomenclatureId) {
      return false
    }

    return Boolean(openedCourses.value?.[nomenclatureId])
  })

  /**
   * Кейсы кнопок, описаны в задаче https://jira.skillbox.pro/browse/WEBDEV-166016
   */
  const buttonCase = computed(() => ({
    case1: Boolean(user?.value && userSubsription?.value && isUserSubscriptionActive.value),
    case2: Boolean(user?.value && userSubsription?.value && !isUserSubscriptionActive.value),
    case3: Boolean(user?.value && !userSubsription?.value),
    case4: !user?.value,
  }))

  const computedProBtn = computed(() => {
    const trialPrice = activeSubscription?.value?.trial_price
    const trialPeriod = activeSubscription?.value?.trial_period || 0
    const basePrice = activeSubscription?.value?.base_price

    if (buttonCase.value.case1) {
      return {
        text: 'Перейти к курсу',
        description: '',
      }
    }

    if (buttonCase.value.case2) {
      return {
        text: 'Пройти по подписке',
        description: `за ${basePrice} ₽ в месяц`,
      }
    }

    if (buttonCase.value.case3 || buttonCase.value.case4) {
      if (props.theme === 'secondary') {
        return {
          text: 'Попробовать',
          description: `за ${trialPrice} ₽ на ${trialPeriod} ${pluralize(trialPeriod, ['день', 'дня', 'дней'])}`,
        }
      } else {
        return {
          text: `Попробовать по подписке за ${trialPrice} ₽`,
          description: `Далее ${basePrice} ₽ в месяц`,
        }
      }
    }
  })

  const handleSubscribe = () => {
    sendLandingClickSubscribeButton({
      nomenclature_id: landing?.value?.commonData.nomenclature_id,
      nomenclature_type: AccessTypeEnum.Paid,
      user_id: Number(user?.value?.id),
    })

    smartComponentsStore.set(SmartComponents.PaymentModal, {
      accessType: AccessTypeEnum.Paid,
      isShow: true,
      isSubcribeProgress: true,
    })
  }

  const openCourse = () => {
    subscribesStore.openCourse({
      nomenclature_id: landing?.value?.commonData.nomenclature_id,
      subscription_plan_id: activeSubscription?.value?.id,
    }).then(() => {
      smartComponentsStore.set(SmartComponents.PaymentModal, {
        isShow: false,
        isSubcribeProgress: false,
        isThanksPopupOpen: true,
        finishModalData: {
          title: 'Ура, курс открыт',
          description: 'Открыли доступ к&nbsp;выбранному курсу. Больше интересного по&nbsp;подписке&nbsp;&mdash; в&nbsp;нашем каталоге. Ищите курсы с&nbsp;пометкой Pro',
          buttonText: 'Начать учиться',
          buttonTheme: 'senary'
        }
      })
    }).catch((error) => {
      smartComponentsStore.set(SmartComponents.PaymentModal, {
        isShow: false,
        isSubcribeProgress: false,
        isThanksPopupOpen: true,
        finishModalData: {
          title: 'Ошибка!',
          description: error.data.message || 'Произошла ошибка',
          buttonText: 'Закрыть',
          buttonTheme: 'senary'
        }
      })
    })
  }

  const handleClick = () => {
    const config = useRuntimeConfig()

    if (hasCourseInSubscriptions?.value && isUserSubscriptionActive.value) {
      location.href = `${config.public.lmsUrl}/education/${landing?.value?.commonData.course_type}/${landing?.value?.commonData.slug}`
    } else if (buttonCase.value.case1) {
      openCourse()
    } else if (buttonCase.value.case2 || buttonCase.value.case3) {
      handleSubscribe()
    } else if (buttonCase.value.case4) {
      isSubscribeOpenModal.value = true
      userStore.login()
    }
  }
</script>
